import React, { useEffect, useMemo, useState } from 'react';
import { API_BASE_URL } from '../../utils/apiBase';
import { formatQuoteMoney } from '../../utils/quotePricing';
import { clientFacingShootType, type StoredQuote } from '../../utils/quoteRecord';
import { formatPreferredDate } from '../../utils/preferredDate';
import { deriveClientJourney, STAGE_TITLES, type JourneyStage } from '../../utils/clientJourney';
import { toShootTab, type ShootTab } from './shootTab';

interface RecordQuote {
  id: string;
  quoteNumber: string;
  status: string;
  updatedAt: string;
  createdAt?: string;
  issuedAt?: string;
  acceptedAt?: string;
  declinedAt?: string;
  preferredDate?: string;
  preferredTime?: string;
  shootType?: string;
  acceptanceChannel?: string;
  deletedAt?: string | null;
  archivedAt?: string | null;
  isTest?: boolean;
  client: { name: string; email: string; phoneE164: string; phone: string; mobile: string; company: string; billingAddress?: string };
  snapshot: { quoteTotalMinor: number };
}

interface RecordBooking {
  quoteId: string;
  shootDate?: string;
  startTime?: string;
  endTime?: string;
  locationName?: string;
  dateConfirmed?: boolean;
  proposedShootDate?: string;
  proposedStartTime?: string;
  completedAt?: string;
  deliveryDueOn?: string;
  deliveredAt?: string;
  deliveryLink?: string;
  closedAt?: string;
}

interface RecordPayment {
  quoteId: string;
  lockedMinor: number;
  outstandingMinor: number;
  receivedMinor: number;
  currentlyDueMinor?: number;
  depositDueMinor?: number;
  depositExpectedMinor?: number;
  depositReceivedMinor?: number;
}

interface ActivityRow {
  id: string;
  at: string;
  kind: string;
  actor: 'client' | 'studio' | 'system';
  quoteId: string;
  quoteNumber?: string;
  title: string;
  detail?: string;
  tab?: ShootTab;
}

interface ClientNotes {
  clientKey: string;
  source: string;
  sourceDetail: string;
  preferences: string;
  notes: string;
  tags: string[];
  updatedAt?: string;
  updatedBy?: string;
}

interface HistoryResponse {
  client: { key: string; name: string; email: string; phoneE164: string; company?: string; billingAddress?: string; quoteCount: number; latestAt: string; firstAt: string };
  record: ClientNotes;
  quotes: RecordQuote[];
  bookings: RecordBooking[];
  payments: RecordPayment[];
  paymentRows: Array<{ id: string; quoteId: string; type: string; verifiedMinor: number; paidAt: string; note?: string }>;
  enquiries: Array<{ id: string; source?: string; shootType?: string; preferredDate?: string; notes?: string; receivedAt?: string; linkedQuoteId?: string | null }>;
  activity: ActivityRow[];
}

const SOURCE_OPTIONS: Array<{ id: string; label: string }> = [
  { id: '', label: 'Not recorded' },
  { id: 'referral', label: 'Referral / word of mouth' },
  { id: 'instagram', label: 'Instagram' },
  { id: 'google', label: 'Google search' },
  { id: 'website', label: 'Website enquiry' },
  { id: 'returning', label: 'Returning client' },
  { id: 'other', label: 'Other' },
];

const rand = (minor?: number) => formatQuoteMoney(minor || 0, 'R');

function when(value?: string) {
  if (!value) return '';
  const ms = Date.parse(value);
  if (!Number.isFinite(ms)) return value;
  return new Intl.DateTimeFormat('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' }).format(new Date(ms));
}

function whenExact(value?: string) {
  if (!value) return '';
  const ms = Date.parse(value);
  if (!Number.isFinite(ms)) return value;
  return new Intl.DateTimeFormat('en-ZA', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }).format(new Date(ms));
}

function dayKey(value: string) {
  const ms = Date.parse(value);
  if (!Number.isFinite(ms)) return value.slice(0, 10);
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Africa/Johannesburg', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date(ms));
}

const ClientRecord = ({
  clientKey,
  onBack,
  onOpenShoot,
  onOpenEnquiry,
  onOpenBuilder,
}: {
  clientKey: string;
  onBack: () => void;
  onOpenShoot?: (quoteId: string, tab?: ShootTab) => void;
  onOpenEnquiry?: (enquiryId: string) => void;
  onOpenBuilder?: () => void;
}) => {
  const [data, setData] = useState<HistoryResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notes, setNotes] = useState<ClientNotes | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState('');
  const [tagDraft, setTagDraft] = useState('');
  const [filter, setFilter] = useState<'all' | 'client' | 'studio'>('all');

  useEffect(() => {
    let active = true;
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const response = await fetch(`${API_BASE_URL}/studio/clients/${encodeURIComponent(clientKey)}/history`);
        const body = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(body.error || 'The client record could not be loaded.');
        if (!active) return;
        setData(body);
        setNotes(body.record);
      } catch (loadError) {
        if (active) setError(loadError instanceof Error ? loadError.message : 'The client record could not be loaded.');
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => {
      active = false;
    };
  }, [clientKey]);

  const jobs = useMemo(() => {
    if (!data) return [];
    return data.quotes
      .filter((quote) => !quote.deletedAt)
      .map((quote) => {
        const booking = data.bookings.find((row) => row.quoteId === quote.id) || null;
        const summary = data.payments.find((row) => row.quoteId === quote.id) || null;
        const journey = deriveClientJourney(quote as unknown as StoredQuote, summary, booking);
        return { quote, booking, summary, journey };
      })
      .sort((left, right) => String(right.quote.updatedAt || '').localeCompare(String(left.quote.updatedAt || '')));
  }, [data]);

  const money = useMemo(() => jobs.reduce((total, job) => ({
    booked: total.booked + (job.summary?.lockedMinor || 0),
    received: total.received + (job.summary?.receivedMinor || 0),
    outstanding: total.outstanding + (job.summary?.outstandingMinor || 0),
  }), { booked: 0, received: 0, outstanding: 0 }), [jobs]);

  const shootsDone = jobs.filter((job) => job.booking?.completedAt).length;
  const openJobs = jobs.filter((job) => !['closed', 'lost'].includes(job.journey.stage));
  const live = openJobs[0] || null;

  const activity = useMemo(() => {
    const rows = (data?.activity || []).filter((row) => filter === 'all' || row.actor === filter);
    const groups: Array<{ day: string; rows: ActivityRow[] }> = [];
    rows.forEach((row) => {
      const day = dayKey(row.at);
      const last = groups[groups.length - 1];
      if (last && last.day === day) last.rows.push(row);
      else groups.push({ day, rows: [row] });
    });
    return groups;
  }, [data, filter]);

  const saveNotes = async () => {
    if (!notes || saving) return;
    setSaving(true);
    setSaved('');
    setError('');
    try {
      const response = await fetch(`${API_BASE_URL}/studio/clients/${encodeURIComponent(clientKey)}/record`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(notes),
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(body.error || 'The notes could not be saved.');
      setNotes(body);
      setSaved(`Saved ${whenExact(body.updatedAt)}`);
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : 'The notes could not be saved.');
    } finally {
      setSaving(false);
    }
  };

  const addTag = () => {
    const tag = tagDraft.trim().toLowerCase();
    if (!tag || !notes) return;
    if (!notes.tags.includes(tag)) setNotes({ ...notes, tags: [...notes.tags, tag].slice(0, 12) });
    setTagDraft('');
  };

  const stageOf = (stage: JourneyStage) => STAGE_TITLES[stage] || stage;

  return (
    <div className="studio-home studio-client-record" data-testid="client-record">
      <header className="studio-home-head">
        <div>
          <button type="button" className="studio-text-action" onClick={onBack} data-testid="client-record-back">Back to clients</button>
          <h2>{data?.client.name || 'Client'}</h2>
          <p>
            {[data?.client.email, data?.client.phoneE164, data?.client.company].filter(Boolean).join(' · ') || 'No contact details'}
            {data?.client.firstAt ? ` · since ${when(data.client.firstAt)}` : ''}
          </p>
          {data?.client.billingAddress ? (
            <p className="studio-billing-address" data-testid="client-billing-address">{data.client.billingAddress}</p>
          ) : null}
        </div>
        <div className="studio-home-actions">
          {data?.client.email ? <a className="studio-secondary" href={`mailto:${data.client.email}`}>Email</a> : null}
          {data?.client.phoneE164 ? <a className="studio-secondary" href={`https://wa.me/${data.client.phoneE164.replace(/\D/g, '')}`} target="_blank" rel="noopener noreferrer">WhatsApp</a> : null}
          {onOpenBuilder ? <button type="button" className="studio-primary" onClick={onOpenBuilder}>New quotation</button> : null}
        </div>
      </header>
      {error ? <p className="studio-error" role="alert">{error}</p> : null}
      {loading ? <p className="studio-note">Gathering the history…</p> : null}
      {data ? (
        <>
          <div className="studio-money studio-finance-totals" data-testid="client-record-totals">
            <div>
              <small>Jobs</small>
              <strong>{jobs.length}</strong>
              <span className="studio-glance-hint">{openJobs.length} open · {shootsDone} shoot{shootsDone === 1 ? '' : 's'} done</span>
            </div>
            <div>
              <small>Booked with us</small>
              <strong>{rand(money.booked)}</strong>
              <span className="studio-glance-hint">across accepted quotations</span>
            </div>
            <div>
              <small>Paid</small>
              <strong>{rand(money.received)}</strong>
              <span className="studio-glance-hint">{money.outstanding > 0 ? `${rand(money.outstanding)} still to collect` : 'nothing outstanding'}</span>
            </div>
            <div className={live?.journey.needsAction ? 'is-live' : ''}>
              <small>Right now</small>
              <strong className="studio-glance-text">{live ? stageOf(live.journey.stage) : (jobs.length ? 'Nothing open' : 'No jobs yet')}</strong>
              <span className="studio-glance-hint">{live ? live.journey.journeyStatus : 'Start with a new quotation'}</span>
            </div>
          </div>

          <section aria-label="Jobs">
            <h3 className="studio-section-label">Jobs</h3>
            {jobs.length === 0 && data.enquiries.length === 0 ? <p className="studio-empty">No jobs or enquiries for this client.</p> : null}
            {jobs.length > 0 ? (
              <div className="studio-table-wrap">
                <table className="studio-table" data-testid="client-record-jobs">
                  <thead>
                    <tr><th>Job</th><th>Stage</th><th>Shoot</th><th>Money</th><th>Next step</th><th></th></tr>
                  </thead>
                  <tbody>
                    {jobs.map(({ quote, booking, summary, journey }) => (
                      <tr key={quote.id} onClick={() => onOpenShoot?.(quote.id, toShootTab(journey.nextTab || 'money'))} style={{ cursor: 'pointer' }} data-testid={`client-record-job-${quote.id}`}>
                        <td data-label="Job">
                          <strong>{quote.quoteNumber}</strong>
                          <div className="studio-row-sub">
                            {[clientFacingShootType(quote.shootType), quote.acceptedAt ? `accepted ${when(quote.acceptedAt)}` : quote.issuedAt ? `sent ${when(quote.issuedAt)}` : `started ${when(quote.createdAt)}`].filter(Boolean).join(' · ')}
                          </div>
                        </td>
                        <td data-label="Stage">
                          <span className={`studio-stage-pill is-${journey.stage}${journey.deliveryOverdue ? ' is-overdue' : ''}`}>{stageOf(journey.stage)}</span>
                          <div className="studio-row-sub">{journey.journeyStatus}</div>
                        </td>
                        <td data-label="Shoot">
                          {booking?.shootDate ? `${formatPreferredDate(booking.shootDate)}${booking.startTime ? ` · ${booking.startTime}` : ''}` : formatPreferredDate(quote.preferredDate) || '—'}
                          {booking?.locationName ? <div className="studio-row-sub">{booking.locationName}</div> : null}
                          {journey.deliveryLine ? <div className="studio-row-sub">{journey.deliveryLine}</div> : null}
                          {booking?.deliveryLink ? <div className="studio-row-sub"><a href={booking.deliveryLink} target="_blank" rel="noopener noreferrer" onClick={(event) => event.stopPropagation()}>Photographs</a></div> : null}
                        </td>
                        <td data-label="Money">
                          {summary ? (
                            <>
                              <span className="studio-amount">{rand(summary.receivedMinor)}</span> of {rand(summary.lockedMinor)}
                              {summary.outstandingMinor > 0 ? <div className="studio-row-sub">{rand(summary.outstandingMinor)} outstanding</div> : <div className="studio-row-sub">paid in full</div>}
                            </>
                          ) : rand(quote.snapshot?.quoteTotalMinor)}
                        </td>
                        <td data-label="Next step">
                          <span className={journey.needsAction ? 'studio-your-move' : 'studio-waiting'}>{journey.nextAction}</span>
                        </td>
                        <td data-label="">
                          <button type="button" className="studio-text-action" onClick={(event) => { event.stopPropagation(); onOpenShoot?.(quote.id, 'money'); }}>Open</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}
            {data.enquiries.filter((row) => !row.linkedQuoteId).length > 0 ? (
              <div className="studio-table-wrap">
                <table className="studio-table">
                  <thead><tr><th>Received</th><th>Asked for</th><th>Note</th><th></th></tr></thead>
                  <tbody>
                    {data.enquiries.filter((row) => !row.linkedQuoteId).map((row) => (
                      <tr key={row.id}>
                        <td data-label="Received">{when(row.receivedAt)}{row.source ? ` · ${row.source}` : ''}</td>
                        <td data-label="Asked for">{[clientFacingShootType(row.shootType), formatPreferredDate(row.preferredDate)].filter(Boolean).join(' · ') || '—'}</td>
                        <td data-label="Note">{row.notes || '—'}</td>
                        <td><button type="button" className="studio-text-action" onClick={() => onOpenEnquiry?.(row.id)}>Open</button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}
          </section>

          <div className="studio-split">
            <section className="studio-panel" aria-label="About this client" data-testid="client-record-notes">
              <h3 className="studio-section-label">About this client</h3>
              {notes ? (
                <>
                  <label>
                    How they found you
                    <select value={notes.source} onChange={(event) => setNotes({ ...notes, source: event.target.value })} aria-label="Source">
                      {SOURCE_OPTIONS.map((option) => <option key={option.id} value={option.id}>{option.label}</option>)}
                    </select>
                  </label>
                  {notes.source === 'referral' || notes.source === 'other' ? (
                    <label>
                      Who or what
                      <input value={notes.sourceDetail} maxLength={200} onChange={(event) => setNotes({ ...notes, sourceDetail: event.target.value })} aria-label="Source detail" />
                    </label>
                  ) : null}
                  <label>
                    Preferences (light, places, children's names, anything to remember)
                    <textarea value={notes.preferences} maxLength={600} rows={3} onChange={(event) => setNotes({ ...notes, preferences: event.target.value })} aria-label="Preferences" />
                  </label>
                  <label>
                    Private notes
                    <textarea value={notes.notes} maxLength={2000} rows={4} onChange={(event) => setNotes({ ...notes, notes: event.target.value })} aria-label="Private notes" />
                  </label>
                  <div className="studio-tags">
                    {notes.tags.map((tag) => (
                      <button type="button" key={tag} className="studio-tag" onClick={() => setNotes({ ...notes, tags: notes.tags.filter((item) => item !== tag) })} title="Remove">
                        {tag} ×
                      </button>
                    ))}
                    <input
                      value={tagDraft}
                      onChange={(event) => setTagDraft(event.target.value)}
                      onKeyDown={(event) => { if (event.key === 'Enter') { event.preventDefault(); addTag(); } }}
                      placeholder="Add a tag and press Enter"
                      aria-label="New tag"
                      maxLength={30}
                    />
                  </div>
                  <div className="studio-toolbar">
                    <button type="button" className="studio-primary" onClick={saveNotes} disabled={saving} data-testid="save-client-record">{saving ? 'Saving…' : 'Save notes'}</button>
                    {saved ? <span className="studio-note">{saved}</span> : notes.updatedAt ? <span className="studio-note">Last saved {whenExact(notes.updatedAt)}</span> : null}
                  </div>
                </>
              ) : null}
            </section>

            <section className="studio-panel studio-activity" aria-label="History" data-testid="client-record-history">
              <div className="studio-toolbar" style={{ justifyContent: 'space-between' }}>
                <h3 className="studio-section-label" style={{ margin: 0 }}>History</h3>
                <div className="studio-view-tabs" role="group" aria-label="Show">
                  <button type="button" className={filter === 'all' ? 'is-active' : ''} aria-pressed={filter === 'all'} onClick={() => setFilter('all')}>All</button>
                  <button type="button" className={filter === 'client' ? 'is-active' : ''} aria-pressed={filter === 'client'} onClick={() => setFilter('client')}>From them</button>
                  <button type="button" className={filter === 'studio' ? 'is-active' : ''} aria-pressed={filter === 'studio'} onClick={() => setFilter('studio')}>From you</button>
                </div>
              </div>
              {activity.length === 0 ? <p className="studio-empty">Nothing recorded yet.</p> : activity.map((group) => (
                <div key={group.day} className="studio-activity-day">
                  <h4>{formatPreferredDate(group.day) || group.day}</h4>
                  {group.rows.map((row) => (
                    <div className={`studio-activity-row is-${row.actor}`} key={row.id}>
                      <span className="studio-activity-when">{new Intl.DateTimeFormat('en-ZA', { hour: '2-digit', minute: '2-digit' }).format(new Date(row.at))}</span>
                      <div>
                        <button type="button" className="studio-text-action" onClick={() => onOpenShoot?.(row.quoteId, toShootTab(row.tab))}>
                          <strong>{row.title}</strong>
                        </button>
                        {jobs.length > 1 && row.quoteNumber ? <span className="studio-row-sub"> {row.quoteNumber}</span> : null}
                        {row.detail ? <div className="studio-card-facts">{row.detail}</div> : null}
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </section>
          </div>
        </>
      ) : null}
    </div>
  );
};

export default ClientRecord;
